import { toast } from "sonner";

// Sonner's default toast lifetime (ms) — used when a caller sets no duration.
const DEFAULT_DURATION = 4000;
// Only ever show this many toasts; a newer one pushes the oldest out.
const MAX_TOASTS = 2;

// Currently-live toasts, NEWEST FIRST — this mirrors sonner's visual stack,
// where data-index 0 is the front (most recent) toast. Each entry is
// { id, expiresAt }.
let active = [];

const prune = (id) => {
  active = active.filter((t) => t.id !== id);
};

const WRAPPED_METHODS = ["success", "error", "warning", "info", "message"];

// Wrap the shared sonner `toast` singleton once so every call site across the
// app inherits: a hard 2-toast cap, expiry tracking (so a toast can be purged
// the instant the app returns to the foreground), and an id registry the tap
// handler uses to close one specific toast.
export function installToastGuard() {
  if (toast.__tmGuarded) return;
  toast.__tmGuarded = true;

  WRAPPED_METHODS.forEach((method) => {
    const orig = toast[method];
    if (typeof orig !== "function") return;
    toast[method] = (message, opts = {}) => {
      const duration = opts.duration ?? DEFAULT_DURATION;
      const userDismiss = opts.onDismiss;
      const userAuto = opts.onAutoClose;
      const id = orig.call(toast, message, {
        ...opts,
        onDismiss: (t) => {
          prune(t?.id);
          if (userDismiss) userDismiss(t);
        },
        onAutoClose: (t) => {
          prune(t?.id);
          if (userAuto) userAuto(t);
        },
      });
      prune(id);
      if (duration !== Infinity) active.unshift({ id, expiresAt: Date.now() + duration });
      // Enforce the cap: drop the oldest until only MAX_TOASTS remain.
      while (active.length > MAX_TOASTS) {
        const oldest = active.pop();
        toast.dismiss(oldest.id);
      }
      return id;
    };
  });
}

// Close the toast shown at visual position `index` (0 = front / newest). Used
// by the tap-to-close handler; falls back to the front toast on a bad index.
export function dismissToastAtIndex(index) {
  const entry = active[Number.isInteger(index) ? index : 0] || active[0];
  if (entry) {
    toast.dismiss(entry.id);
    prune(entry.id);
  }
}

// Drop every toast whose time has already run out. Called when the app comes
// back to the foreground, because the browser throttles sonner's auto-dismiss
// timer while the tab is hidden, leaving stale toasts stuck on screen.
export function dismissExpiredToasts() {
  const now = Date.now();
  active
    .filter((t) => now >= t.expiresAt)
    .forEach((t) => {
      toast.dismiss(t.id);
      prune(t.id);
    });
}
